import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Button,
  Card,
  Col,
  DatePicker,
  Descriptions,
  Form,
  Input,
  InputNumber,
  Tooltip,
  Row,
  Space,
  Spin,
  Switch,
  Table,
  Tag,
  Typography,
  message,
} from 'antd';
import { ArrowLeftOutlined, PrinterOutlined, SaveOutlined, ThunderboltOutlined } from '@ant-design/icons';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useParams } from 'react-router-dom';
import dayjs, { type Dayjs } from 'dayjs';
import { ApprovalPanel } from '@/components/approval/ApprovalPanel';
import { ConvertCertificationToSalesInvoiceModal } from '@/components/epc/ConvertCertificationToSalesInvoiceModal';
import { PrintableIpcPaymentApplication } from '@/components/epc/PrintableIpcPaymentApplication';
import EntityAttachmentsCard from '@/components/documents/EntityAttachmentsCard';
import { DepartmentSelect } from '@/components/finance/DepartmentSelect';
import { ENTITY_TYPE_EPC_PAYMENT_APPLICATION } from '@/constants/approval-entity-types';
import { DOCUMENT_ENTITY_TYPE_EPC_PAYMENT_APPLICATION } from '@/constants/document-entity-types';
import {
  commercialModelInfo,
  lineOverClaim,
  lineOverClaimPolicy,
  type OverClaimSeverity,
} from '@/constants/epcCommercialModels';
import {
  epcContractAdminService,
  type EpcIpcLineProgressEvidence,
} from '@/services/epcContractAdmin.service';
import { formatMoney } from '@/utils/format';
import { previewIpcValuation } from '@/utils/ipcValuationPreview';
import { openPmvPrintOverlay } from '@/utils/openPmvPrintOverlay';

const { Title, Text } = Typography;

type IpcLineDraft = {
  key: string;
  seedKey: string;
  milestoneId: string;
  sovLineId: string;
  lineCode: string;
  description: string;
  unit: string;
  contractQty: number | null;
  previousQty: number | null;
  lineAmount: number;
  previousCertified: number;
  remaining: number;
  thisPeriodPct: number;
  thisPeriodQty: number;
  unitRate: number | null;
  appliedAmount: number;
  evidenceRef: string;
  evidenceNote: string;
};

function apiErr(e: unknown): string {
  const ax = e as {
    response?: { data?: { message?: string; errors?: { message?: string } } };
    message?: string;
  };
  const data = ax?.response?.data;
  return data?.message || data?.errors?.message || ax?.message || 'Request failed';
}

function ymd(d: Dayjs | null | undefined): string | undefined {
  if (!d) return undefined;
  return d.format('YYYY-MM-DD');
}

function round2(n: number): number {
  return Math.round((Number(n) || 0) * 100) / 100;
}

/** Same tolerance as the server ABOVE_SITE_PROGRESS warning. */
const SITE_PROGRESS_TOLERANCE_PCT = 2;

const EVIDENCE_UNAVAILABLE: Record<string, string> = {
  cep_disabled: 'Construction Execution is disabled — site progress hints are not available.',
  cep_unavailable: 'Construction Execution is not installed — site progress hints are not available.',
  no_project: 'No project is linked to this contract (mobilization or contract project) — no site progress to compare.',
  error: 'Site progress could not be loaded.',
};

function money(v: unknown, currency?: string) {
  return formatMoney(v, { currency: currency || 'AED' });
}

const EpcPaymentApplicationFormPage: React.FC = () => {
  const { id: contractId, paId } = useParams<{ id: string; paId?: string }>();
  const isNew = !paId || paId === 'new';
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [form] = Form.useForm();
  const [lines, setLines] = useState<IpcLineDraft[]>([]);
  const [useAdvanceRate, setUseAdvanceRate] = useState(true);
  const [useRetentionRate, setUseRetentionRate] = useState(true);
  const [convertOpen, setConvertOpen] = useState(false);

  const draftQ = useQuery({
    queryKey: ['epc-pa-draft', contractId, isNew ? 'new' : paId],
    queryFn: () =>
      epcContractAdminService.getPaymentApplicationDraft(
        contractId!,
        isNew ? undefined : paId
      ),
    enabled: Boolean(contractId),
  });

  const paQ = useQuery({
    queryKey: ['epc-pa', paId],
    queryFn: () => epcContractAdminService.getPaymentApplication(paId!),
    enabled: Boolean(paId) && !isNew,
  });

  const certsQ = useQuery({
    queryKey: ['epc-contract-admin-certs', contractId],
    queryFn: () => epcContractAdminService.listCertifications(contractId!),
    enabled: Boolean(contractId) && !isNew,
  });

  const draft = draftQ.data as Record<string, unknown> | undefined;
  const draftContract = draft?.contract as Record<string, unknown> | undefined;
  const defaults = draft?.defaults as Record<string, unknown> | undefined;
  const currency = String(
    paQ.data?.currency || defaults?.currency || draftContract?.currency || 'AED'
  );
  const status = String(paQ.data?.status || 'Draft');
  const editable = isNew || status === 'Draft' || status === 'Rejected';
  const pendingApplications = ((draft?.pendingApplications as
    | Array<{ id: string; applicationNumber: string; status: string; grossAppliedAmount: number }>
    | undefined) ?? []).filter((p) => p.id !== paId);

  const linkedCert = useMemo(() => {
    if (!paId || isNew) return null;
    const rows = (certsQ.data || []) as Record<string, unknown>[];
    return rows.find((c) => String(c.payment_application_id) === String(paId)) || null;
  }, [certsQ.data, paId, isNew]);

  useEffect(() => {
    if (!draft || (!isNew && !paQ.data)) return;

    if (isNew) {
      const seedLines = ((draft.lines as Record<string, unknown>[]) || []).map((ln, i) => {
        const lineAmount = Number(ln.lineAmount) || 0;
        const previousCertified = Number(ln.previousCertifiedAmount) || 0;
        const remaining = Number(ln.remainingAmount) || Math.max(0, lineAmount - previousCertified);
        return {
          key: String(ln.seedKey || ln.milestoneId || `seed-${i}`),
          seedKey: String(ln.seedKey || ''),
          milestoneId: ln.milestoneId ? String(ln.milestoneId) : '',
          sovLineId: ln.sovLineId ? String(ln.sovLineId) : '',
          lineCode: String(ln.lineCode || ''),
          description: String(ln.description || ''),
          unit: ln.unit ? String(ln.unit) : '',
          contractQty: ln.sovLineId && ln.qty != null ? Number(ln.qty) : null,
          previousQty: ln.previousQuantity != null ? Number(ln.previousQuantity) : null,
          lineAmount,
          previousCertified,
          remaining,
          thisPeriodPct: 0,
          thisPeriodQty: 0,
          unitRate: ln.unitRate != null ? Number(ln.unitRate) : lineAmount || null,
          appliedAmount: 0,
          evidenceRef: '',
          evidenceNote: '',
        } as IpcLineDraft;
      });
      setLines(seedLines);
      const advPct = defaults?.advanceRecoveryRatePct != null;
      const retPct = defaults?.retentionRatePct != null;
      setUseAdvanceRate(!!advPct);
      setUseRetentionRate(!!retPct);
      form.setFieldsValue({
        currency,
        department_id: defaults?.departmentId || draftContract?.departmentId || undefined,
        periodFrom: dayjs().startOf('month'),
        periodTo: dayjs(),
        advanceRecoveryRatePct: defaults?.advanceRecoveryRatePct ?? undefined,
        advanceRecoveryAmount: 0,
        remainingAdvanceBalance: defaults?.remainingAdvanceBalance ?? undefined,
        retentionRatePct: defaults?.retentionRatePct ?? undefined,
        retentionWithheldAmount: 0,
        retentionReleasedAmount: 0,
        retentionCap: defaults?.retentionCap ?? undefined,
      });
      return;
    }

    const pa = paQ.data!;
    const paLines = ((pa.lines as Record<string, unknown>[]) || []).map((ln, i) => {
      const meta =
        typeof ln.meta_json === 'string'
          ? (() => {
              try {
                return JSON.parse(String(ln.meta_json));
              } catch {
                return {};
              }
            })()
          : (ln.meta_json as Record<string, unknown>) || {};
      const lineAmount = Number(meta.lineAmount ?? ln.unit_rate ?? ln.applied_amount) || 0;
      const previousCertified = Number(ln.previous_certified_amount) || 0;
      const appliedAmount = Number(ln.applied_amount) || 0;
      const remaining = Math.max(0, round2(lineAmount - previousCertified));
      const pct =
        lineAmount > 0 ? round2((appliedAmount / lineAmount) * 100) : Number(meta.thisPeriodPct) || 0;
      return {
        key: String(ln.id || meta.seedKey || `ln-${i}`),
        seedKey: String(meta.seedKey || ''),
        milestoneId: ln.milestone_id ? String(ln.milestone_id) : '',
        sovLineId: ln.sov_line_id ? String(ln.sov_line_id) : '',
        lineCode: String(ln.line_code || ''),
        description: String(ln.description || ''),
        unit: meta.unit ? String(meta.unit) : '',
        contractQty: meta.contractQty != null ? Number(meta.contractQty) : null,
        previousQty: ln.previous_quantity != null ? Number(ln.previous_quantity) : null,
        lineAmount,
        previousCertified,
        remaining,
        thisPeriodPct: pct,
        thisPeriodQty: Number(meta.thisPeriodQty ?? ln.quantity) || 0,
        unitRate: ln.unit_rate != null ? Number(ln.unit_rate) : null,
        appliedAmount,
        evidenceRef: meta.evidenceRef ? String(meta.evidenceRef) : '',
        evidenceNote: meta.evidenceNote ? String(meta.evidenceNote) : '',
      } as IpcLineDraft;
    });
    setLines(paLines);

    const terms =
      typeof pa.commercial_terms_json === 'string'
        ? (() => {
            try {
              return JSON.parse(String(pa.commercial_terms_json));
            } catch {
              return {};
            }
          })()
        : (pa.commercial_terms_json as Record<string, unknown>) || {};
    const hasAdvRate = terms.advanceRecoveryRatePct != null;
    const hasRetRate = terms.retentionRatePct != null;
    setUseAdvanceRate(hasAdvRate);
    setUseRetentionRate(hasRetRate);
    form.setFieldsValue({
      currency: pa.currency || currency,
      department_id: pa.department_id || undefined,
      periodFrom: pa.period_from ? dayjs(String(pa.period_from)) : null,
      periodTo: pa.period_to ? dayjs(String(pa.period_to)) : null,
      advanceRecoveryRatePct: terms.advanceRecoveryRatePct ?? undefined,
      advanceRecoveryAmount: Number(pa.advance_recovery_amount) || 0,
      remainingAdvanceBalance:
        terms.remainingAdvanceBalance ?? defaults?.remainingAdvanceBalance ?? undefined,
      retentionRatePct: terms.retentionRatePct ?? undefined,
      retentionWithheldAmount: Number(pa.retention_withheld_amount) || 0,
      retentionReleasedAmount: Number(pa.retention_released_amount) || 0,
      retentionCap: terms.retentionCap ?? defaults?.retentionCap ?? undefined,
    });
  }, [draft, paQ.data, isNew, form, currency, defaults, draftContract]);

  const workGross = useMemo(
    () => round2(lines.reduce((s, l) => s + (Number(l.appliedAmount) || 0), 0)),
    [lines]
  );
  const openingPosition = draft?.openingPosition as
    | { notAllocatedToLines?: number; lastIpcNumber?: string | null; asOfDate?: string | null }
    | null
    | undefined;
  const openingNotOnLines = Number(openingPosition?.notAllocatedToLines) || 0;
  const isSovBasis = draft?.measurementBasis === 'SOV';
  const commercialModel = (draft?.commercialModel as string | null | undefined) ?? null;
  const modelInfo = commercialModelInfo(commercialModel);
  const capped = (ln: Pick<IpcLineDraft, 'unit'>) => lineOverClaimPolicy(commercialModel, ln.unit) === 'CAP_WARN';
  const overClaimOf = (ln: IpcLineDraft) =>
    lineOverClaim(commercialModel, {
      unit: ln.unit,
      lineAmount: ln.lineAmount,
      previousCertified: ln.previousCertified,
      appliedAmount: ln.appliedAmount,
      contractQty: ln.contractQty,
      cumulativeQty: ln.sovLineId ? (ln.previousQty ?? 0) + (ln.thisPeriodQty || 0) : null,
    });
  const overClaims = lines
    .map((ln) => ({ ln, severity: overClaimOf(ln) }))
    .filter((x): x is { ln: IpcLineDraft; severity: OverClaimSeverity } => x.severity != null);
  const { milestoneGateBySeed, variationSeeds } = useMemo(() => {
    const gates = new Map<string, string>();
    const vos = new Set<string>();
    for (const ln of (draft?.lines as Record<string, unknown>[] | undefined) ?? []) {
      const seed = String(ln.seedKey || '');
      if (ln.milestoneNumber && ln.milestoneAchieved === false) gates.set(seed, String(ln.milestoneNumber));
      if (ln.isVariation) vos.add(seed);
    }
    return { milestoneGateBySeed: gates, variationSeeds: vos };
  }, [draft]);
  const unachievedMilestoneOf = (ln: IpcLineDraft) =>
    ln.appliedAmount > 0 ? milestoneGateBySeed.get(ln.seedKey) ?? null : null;
  const unachievedClaims = lines.filter((ln) => unachievedMilestoneOf(ln));
  const feeCheck = useMemo(() => {
    const pct = Number(draft?.feePct);
    if (modelInfo?.key !== 'COST_PLUS' || !Number.isFinite(pct) || pct <= 0) return null;
    const isFee = (l: IpcLineDraft) => /\bfee\b/i.test(l.description);
    const feeLines = lines.filter(isFee);
    if (!feeLines.length) return null;
    const fee = round2(feeLines.reduce((s, l) => s + (l.appliedAmount || 0), 0));
    const cost = round2(lines.filter((l) => !isFee(l)).reduce((s, l) => s + (l.appliedAmount || 0), 0));
    const expected = round2((cost * pct) / 100);
    return Math.abs(fee - expected) <= Math.max(1, expected * 0.005) ? null : { pct, fee, cost, expected };
  }, [draft?.feePct, modelInfo?.key, lines]);
  const previousGross = useMemo(
    () =>
      round2(
        lines.reduce((s, l) => s + (Number(l.previousCertified) || 0), 0) + openingNotOnLines
      ),
    [lines, openingNotOnLines]
  );

  const periodTo = Form.useWatch('periodTo', form) as Dayjs | null | undefined;
  const periodToYmd = ymd(periodTo) ?? null;
  const evidenceQ = useQuery({
    queryKey: ['epc-ipc-progress-evidence', contractId, isNew ? 'new' : paId, periodToYmd],
    queryFn: () =>
      epcContractAdminService.getIpcProgressEvidence(contractId!, {
        periodTo: periodToYmd,
        excludeApplicationId: isNew ? undefined : paId,
      }),
    enabled: Boolean(contractId),
  });
  const evidence = evidenceQ.data;
  const evidenceBySeed = useMemo(
    () => new Map((evidence?.lines ?? []).map((e) => [e.seedKey, e])),
    [evidence]
  );
  const seedOf = (ln: IpcLineDraft) =>
    ln.seedKey || (ln.sovLineId ? `sov:${ln.sovLineId}` : ln.milestoneId ? `ms:${ln.milestoneId}` : '');
  const evidenceOf = (ln: IpcLineDraft): EpcIpcLineProgressEvidence | undefined => evidenceBySeed.get(seedOf(ln));
  const cumulativePctOf = (ln: IpcLineDraft) =>
    ln.lineAmount > 0 ? round2(((ln.previousCertified + ln.appliedAmount) / ln.lineAmount) * 100) : 0;
  const aboveSite = lines.filter((ln) => {
    const ev = evidenceOf(ln);
    return (
      ln.appliedAmount > 0 &&
      !ev?.milestone &&
      ev?.matched &&
      ev.siteCumulativePct != null &&
      cumulativePctOf(ln) > ev.siteCumulativePct + SITE_PROGRESS_TOLERANCE_PCT
    );
  });
  const withoutEvidence = lines.filter((ln) => {
    const ev = evidenceOf(ln);
    return ln.appliedAmount > 0 && ln.lineAmount > 0 && !ev?.milestone && !ev?.matched && !ln.evidenceRef.trim();
  });
  const applicableSuggestions = lines.filter((ln) => {
    const ev = evidenceOf(ln);
    return ev?.matched && ev.suggestedThisPeriodPct != null && ev.suggestedThisPeriodPct !== ln.thisPeriodPct;
  });
  const applySiteProgress = () => {
    for (const ln of applicableSuggestions) {
      updateLine(ln.key, { thisPeriodPct: evidenceOf(ln)!.suggestedThisPeriodPct! });
    }
    message.success(
      `Applied site progress to ${applicableSuggestions.length} line${applicableSuggestions.length === 1 ? '' : 's'}`
    );
  };

  const advRate = Form.useWatch('advanceRecoveryRatePct', form);
  const advAmt = Form.useWatch('advanceRecoveryAmount', form);
  const remAdv = Form.useWatch('remainingAdvanceBalance', form);
  const retRate = Form.useWatch('retentionRatePct', form);
  const retAmt = Form.useWatch('retentionWithheldAmount', form);
  const retRel = Form.useWatch('retentionReleasedAmount', form);
  const retCap = Form.useWatch('retentionCap', form);

  const preview = useMemo(
    () =>
      previewIpcValuation({
        grossApplied: workGross,
        advanceRecoveryRatePct: useAdvanceRate ? Number(advRate) || undefined : undefined,
        advanceRecoveryAmount: !useAdvanceRate ? Number(advAmt) || 0 : undefined,
        remainingAdvanceBalance: remAdv != null ? Number(remAdv) : undefined,
        retentionRatePct: useRetentionRate ? Number(retRate) || undefined : undefined,
        retentionWithheldAmount: !useRetentionRate ? Number(retAmt) || 0 : undefined,
        retentionReleasedAmount: Number(retRel) || 0,
        retentionCap: retCap != null ? Number(retCap) : undefined,
      }),
    [workGross, useAdvanceRate, advRate, advAmt, remAdv, useRetentionRate, retRate, retAmt, retRel, retCap]
  );

  const updateLine = (key: string, patch: Partial<IpcLineDraft>) => {
    setLines((prev) =>
      prev.map((ln) => {
        if (ln.key !== key) return ln;
        const next = { ...ln, ...patch };
        const cap = capped(ln);
        if (patch.thisPeriodPct != null) {
          const pct = Math.max(0, cap ? Math.min(100, Number(patch.thisPeriodPct) || 0) : Number(patch.thisPeriodPct) || 0);
          let applied = round2((ln.lineAmount * pct) / 100);
          if (cap && applied > ln.remaining + 0.005) applied = ln.remaining;
          next.thisPeriodPct = pct;
          next.appliedAmount = applied;
          if (ln.unitRate && ln.unitRate > 0) {
            next.thisPeriodQty = round2(applied / ln.unitRate);
          }
        } else if (patch.appliedAmount != null) {
          let applied = Math.max(0, round2(Number(patch.appliedAmount) || 0));
          if (cap && applied > ln.remaining + 0.005) applied = ln.remaining;
          next.appliedAmount = applied;
          next.thisPeriodPct =
            ln.lineAmount > 0 ? round2((applied / ln.lineAmount) * 100) : 0;
        } else if (patch.thisPeriodQty != null && ln.unitRate && ln.unitRate > 0) {
          const qty = Math.max(0, Number(patch.thisPeriodQty) || 0);
          let applied = round2(qty * ln.unitRate);
          if (cap && applied > ln.remaining + 0.005) {
            applied = ln.remaining;
          }
          next.thisPeriodQty = qty;
          next.appliedAmount = applied;
          next.thisPeriodPct =
            ln.lineAmount > 0 ? round2((applied / ln.lineAmount) * 100) : 0;
        }
        return next;
      })
    );
  };

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['epc-contract-admin-workspace', contractId] });
    qc.invalidateQueries({ queryKey: ['epc-contract-admin-pa', contractId] });
    qc.invalidateQueries({ queryKey: ['epc-contract-admin-certs', contractId] });
    qc.invalidateQueries({ queryKey: ['epc-pa-draft', contractId] });
    qc.invalidateQueries({ queryKey: ['epc-ipc-progress-evidence', contractId] });
    qc.invalidateQueries({ queryKey: ['epc-ipc-register'] });
    qc.invalidateQueries({ queryKey: ['epc-certifications-register'] });
    if (paId && !isNew) qc.invalidateQueries({ queryKey: ['epc-pa', paId] });
  };

  const buildPayload = (values: Record<string, unknown>) => {
    const payloadLines = lines
      .filter((ln) => (Number(ln.appliedAmount) || 0) > 0 || isNew)
      .map((ln) => ({
        milestoneId: ln.milestoneId || undefined,
        sovLineId: ln.sovLineId || undefined,
        previousQuantity: ln.sovLineId ? ln.previousQty ?? 0 : undefined,
        lineCode: ln.lineCode || undefined,
        description: ln.description,
        quantity: ln.thisPeriodQty || undefined,
        unitRate: ln.unitRate ?? undefined,
        appliedAmount: round2(ln.appliedAmount),
        previousCertifiedAmount: round2(ln.previousCertified),
        thisPeriodPct: ln.thisPeriodPct,
        thisPeriodQty: ln.thisPeriodQty,
        seedKey: ln.seedKey || undefined,
        lineAmount: ln.lineAmount,
        metaJson: {
          thisPeriodPct: ln.thisPeriodPct,
          thisPeriodQty: ln.thisPeriodQty,
          lineAmount: ln.lineAmount,
          seedKey: ln.seedKey,
          unit: ln.unit || undefined,
          contractQty: ln.contractQty ?? undefined,
          cumulativeAmount: round2(ln.previousCertified + ln.appliedAmount),
          evidenceRef: ln.evidenceRef.trim() || undefined,
          evidenceNote: ln.evidenceNote.trim() || undefined,
          ...(evidenceOf(ln)?.matched
            ? {
                siteCumulativePct: evidenceOf(ln)!.siteCumulativePct ?? undefined,
                progressAsOf: evidence?.asOf ?? undefined,
                packageIds: evidenceOf(ln)!.packages.map((p) => p.id),
              }
            : {}),
        },
      }));
    const gross = round2(payloadLines.reduce((s, l) => s + l.appliedAmount, 0));
    return {
      contractId,
      departmentId: values.department_id,
      currency: values.currency || currency,
      periodFrom: ymd(values.periodFrom as Dayjs | null),
      periodTo: ymd(values.periodTo as Dayjs | null),
      grossAppliedAmount: gross,
      advanceRecoveryRatePct: useAdvanceRate
        ? values.advanceRecoveryRatePct != null
          ? Number(values.advanceRecoveryRatePct)
          : undefined
        : undefined,
      advanceRecoveryAmount: !useAdvanceRate
        ? values.advanceRecoveryAmount != null
          ? Number(values.advanceRecoveryAmount)
          : 0
        : undefined,
      remainingAdvanceBalance:
        values.remainingAdvanceBalance != null
          ? Number(values.remainingAdvanceBalance)
          : undefined,
      retentionRatePct: useRetentionRate
        ? values.retentionRatePct != null
          ? Number(values.retentionRatePct)
          : undefined
        : undefined,
      retentionWithheldAmount: !useRetentionRate
        ? values.retentionWithheldAmount != null
          ? Number(values.retentionWithheldAmount)
          : 0
        : undefined,
      retentionReleasedAmount:
        values.retentionReleasedAmount != null ? Number(values.retentionReleasedAmount) : 0,
      retentionCap: values.retentionCap != null ? Number(values.retentionCap) : undefined,
      previousGrossCertified: previousGross,
      cumulativeGrossAmount: round2(previousGross + gross),
      commercialTerms: {
        advanceRecoveryRatePct: useAdvanceRate ? values.advanceRecoveryRatePct : undefined,
        retentionRatePct: useRetentionRate ? values.retentionRatePct : undefined,
        remainingAdvanceBalance: values.remainingAdvanceBalance,
        retentionCap: values.retentionCap,
        previousGrossCertified: previousGross,
      },
      lines: payloadLines.filter((l) => l.appliedAmount > 0),
    };
  };

  const saveM = useMutation({
    mutationFn: async (values: Record<string, unknown>) => {
      const payload = buildPayload(values);
      if (!payload.lines.length || Number(payload.grossAppliedAmount) <= 0) {
        throw new Error('Enter this-period progress % or amount on at least one line');
      }
      if (isNew) return epcContractAdminService.createPaymentApplication(payload);
      return epcContractAdminService.patchPaymentApplication(paId!, payload);
    },
    onSuccess: (saved) => {
      message.success(isNew ? 'Payment application created' : 'Payment application saved');
      const warnings = (saved as { overClaimWarnings?: unknown[] } | undefined)?.overClaimWarnings;
      if (warnings?.length) {
        message.warning(`${warnings.length} over-claim warning${warnings.length === 1 ? '' : 's'} recorded for the approver`);
      }
      invalidate();
      const newId = String(saved?.id || paId);
      navigate(`/epc/commercial/contracts/${contractId}/payment-applications/${newId}`, {
        replace: true,
      });
    },
    onError: (e: unknown) => message.error(apiErr(e)),
  });

  const submitM = useMutation({
    mutationFn: () => epcContractAdminService.submitPaymentApplication(paId!),
    onSuccess: () => {
      message.success('IPC submitted for approval');
      invalidate();
    },
    onError: (e: unknown) => message.error(apiErr(e)),
  });

  const createCertM = useMutation({
    mutationFn: () =>
      epcContractAdminService.createCertification({ paymentApplicationId: paId }),
    onSuccess: () => {
      message.success('Certification created');
      invalidate();
    },
    onError: (e: unknown) => message.error(apiErr(e)),
  });

  const confirmCertM = useMutation({
    mutationFn: (certId: string) => epcContractAdminService.confirmCertification(certId),
    onSuccess: () => {
      message.success('Certification confirmed');
      invalidate();
    },
    onError: (e: unknown) => message.error(apiErr(e)),
  });

  const convertSiM = useMutation({
    mutationFn: (departmentId: string) =>
      epcContractAdminService.convertCertificationToSalesInvoice(String(linkedCert!.id), {
        departmentId,
      }),
    onSuccess: (data) => {
      message.success('Draft Progress Tax Invoice ready');
      setConvertOpen(false);
      invalidate();
      const row = data as Record<string, unknown>;
      const bridge = row.salesInvoice as Record<string, unknown> | undefined;
      const siId = bridge?.salesInvoiceId || linkedCert?.sales_invoice_id;
      if (siId) navigate(`/growth/invoices/${String(siId)}`);
    },
    onError: (e: unknown) => message.error(apiErr(e)),
  });

  const printIpc = async () => {
    try {
      if (isNew || !paId) {
        message.info('Save the payment application before printing');
        return;
      }
      const application = await epcContractAdminService.getPaymentApplication(paId);
      openPmvPrintOverlay((onClose) => (
        <PrintableIpcPaymentApplication
          application={application}
          contract={
            draftContract
              ? {
                  ...draftContract,
                  contract_number: draftContract.contractNumber ?? draftContract.contract_number,
                  contract_value: draftContract.contractValue ?? draftContract.contract_value,
                  customer_name: draftContract.customerName ?? draftContract.customer_name,
                  project_label: draftContract.projectLabel ?? draftContract.project_label,
                }
              : undefined
          }
          customerName={
            draftContract?.customerName
              ? String(draftContract.customerName)
              : draftContract?.customer_name
                ? String(draftContract.customer_name)
                : undefined
          }
          projectLabel={
            draftContract?.projectLabel
              ? String(draftContract.projectLabel)
              : draftContract?.project_label
                ? String(draftContract.project_label)
                : undefined
          }
          onClose={onClose}
        />
      ));
    } catch (e: unknown) {
      message.error(apiErr(e));
    }
  };

  if (draftQ.isLoading || (!isNew && paQ.isLoading)) {
    return (
      <div style={{ padding: 48, textAlign: 'center' }}>
        <Spin size="large" />
      </div>
    );
  }

  if (draftQ.isError) {
    return (
      <div style={{ padding: 16 }}>
        <Alert type="error" showIcon message={apiErr(draftQ.error)} />
      </div>
    );
  }

  const certStatus = linkedCert ? String(linkedCert.status) : '';
  const siId = linkedCert?.sales_invoice_id ? String(linkedCert.sales_invoice_id) : '';

  return (
    <div style={{ padding: 16, maxWidth: 1200, margin: '0 auto' }}>
      <Space
        style={{ marginBottom: 16, width: '100%', justifyContent: 'space-between' }}
        wrap
      >
        <Space wrap>
          <Button
            type="text"
            icon={<ArrowLeftOutlined />}
            onClick={() => navigate(`/epc/commercial/contracts/${contractId}?caTab=progress`)}
          >
            Back to workspace
          </Button>
          <div>
            <Title level={3} style={{ margin: 0 }}>
              {isNew
                ? 'New Payment Application (IPC)'
                : `IPC ${String(paQ.data?.application_number || '')}`}
            </Title>
            <Text type="secondary">
              Progress billing for {String(draftContract?.contractNumber || contractId)}
              {!isNew ? ` · ${status}` : ''}
            </Text>
          </div>
        </Space>
        {!isNew ? (
          <Button
            id="ipc-print-header"
            type="primary"
            icon={<PrinterOutlined />}
            onClick={() => void printIpc()}
          >
            Print for client
          </Button>
        ) : null}
      </Space>

      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
        message="Progress billing"
        description={
          <span>
            Enter <strong>this-period %</strong> (or amount) per entitlement line. Previous certified
            is deducted automatically. Advance / retention use contract particulars rates when set.
            VAT is applied later on the Progress Tax Invoice — not on the IPC.
            {modelInfo ? (
              <>
                <br />
                <strong>{modelInfo.label}:</strong> {modelInfo.ipcBasis}
              </>
            ) : null}
          </span>
        }
      />
      {pendingApplications.length && editable ? (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
          message="Other IPCs on this contract are not certified yet"
          description={`${pendingApplications
            .map((p) => `${p.applicationNumber} (${p.status}, ${money(p.grossAppliedAmount, currency)})`)
            .join(', ')}. Previous certified excludes them; over-claim checks on save count them as claimed.`}
        />
      ) : null}

      <Descriptions size="small" bordered column={3} style={{ marginBottom: 16 }}>
        <Descriptions.Item label="Contract value">
          {money(draftContract?.contractValue, currency)}
        </Descriptions.Item>
        <Descriptions.Item label="Previous certified">
          {money(previousGross, currency)}
        </Descriptions.Item>
        <Descriptions.Item label="Remaining entitlement">
          {money(Math.max(0, Number(draftContract?.contractValue || 0) - previousGross), currency)}
        </Descriptions.Item>
        <Descriptions.Item label="Advance recovered to date">
          {money(defaults?.advanceRecoveredToDate, currency)}
        </Descriptions.Item>
        <Descriptions.Item label="Remaining advance">
          {money(defaults?.remainingAdvanceBalance, currency)}
          {defaults?.advanceBasis === 'CONTRACT_PCT' && Number(defaults?.advanceEntitled) > 0 ? (
            <div>
              <Text type="secondary">No advance billed yet — limit is the contract advance %.</Text>
            </div>
          ) : null}
        </Descriptions.Item>
        <Descriptions.Item label="Retention held / cap">
          {money(defaults?.retentionHeldToDate, currency)} / {money(defaults?.retentionCap, currency)}
        </Descriptions.Item>
        {openingPosition ? (
          <Descriptions.Item label="Opening position" span={3}>
            Includes certification before this system
            {openingPosition.lastIpcNumber ? ` up to ${openingPosition.lastIpcNumber}` : ''}
            {openingPosition.asOfDate ? ` (as of ${String(openingPosition.asOfDate).slice(0, 10)})` : ''}
            {openingNotOnLines > 0
              ? ` · ${money(openingNotOnLines, currency)} not allocated to lines`
              : ''}
          </Descriptions.Item>
        ) : null}
      </Descriptions>

      <Form
        form={form}
        layout="vertical"
        onFinish={(values) => saveM.mutate(values)}
        disabled={!editable}
      >
        <Card title="Period & department" style={{ marginBottom: 16 }}>
          <Row gutter={16}>
            <Col xs={24} md={8}>
              <Form.Item
                name="department_id"
                label="Department"
                rules={[{ required: true, message: 'Department is required' }]}
              >
                <DepartmentSelect />
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item name="periodFrom" label="Period from">
                <DatePicker style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item name="periodTo" label="Period to">
                <DatePicker style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>
        </Card>

        <Card
          title="Entitlement lines (this period)"
          style={{ marginBottom: 16 }}
          extra={
            editable && evidence?.available ? (
              <Tooltip title="Sets this-period % = site cumulative % at the period end minus previously certified %, for lines linked to execution packages.">
                <Button
                  size="small"
                  icon={<ThunderboltOutlined />}
                  disabled={!applicableSuggestions.length}
                  onClick={applySiteProgress}
                >
                  Apply site progress
                </Button>
              </Tooltip>
            ) : null
          }
        >
          {evidence && !evidence.available && evidence.reason ? (
            <Alert
              type="info"
              showIcon
              style={{ marginBottom: 12 }}
              message={EVIDENCE_UNAVAILABLE[evidence.reason] ?? EVIDENCE_UNAVAILABLE.error}
              description="Enter an evidence reference per line (measurement sheet, inspection report, survey) and attach the documents to the IPC."
            />
          ) : null}
          {evidence?.available ? (
            <Text type="secondary" style={{ display: 'block', marginBottom: 8 }}>
              Site progress as of {evidence.asOf ?? 'today'}: {evidence.matchedLines} line
              {evidence.matchedLines === 1 ? '' : 's'} linked to {evidence.packageCount} execution package
              {evidence.packageCount === 1 ? '' : 's'} by WBS code
              {evidence.unmatchedSovLines ? `; ${evidence.unmatchedSovLines} SOV line(s) have no matching package` : ''}.
              Site % = installed quantity in daily logs / planned quantity.
            </Text>
          ) : null}
          <Table
            size="small"
            rowKey="key"
            pagination={false}
            scroll={{ x: 1500 }}
            dataSource={lines}
            columns={[
              {
                title: 'Code',
                dataIndex: 'lineCode',
                width: 110,
                render: (v: string, r: IpcLineDraft) => (
                  <Space direction="vertical" size={0}>
                    <span>{v}</span>
                    {variationSeeds.has(r.seedKey) ? <Tag color="purple">Variation</Tag> : null}
                  </Space>
                ),
              },
              { title: 'Description', dataIndex: 'description', width: 220 },
              ...(isSovBasis
                ? [
                    { title: 'Unit', dataIndex: 'unit', width: 70 },
                    {
                      title: 'Contract qty',
                      dataIndex: 'contractQty',
                      width: 100,
                      render: (v: number | null) => (v == null ? '—' : v),
                    },
                    {
                      title: 'Prev qty',
                      dataIndex: 'previousQty',
                      width: 90,
                      render: (v: number | null) => (v == null ? '—' : v),
                    },
                    {
                      title: 'This period qty',
                      dataIndex: 'thisPeriodQty',
                      width: 130,
                      render: (v: number, r: IpcLineDraft) => (
                        <InputNumber
                          min={0}
                          value={v}
                          disabled={!editable || !r.unitRate}
                          style={{ width: '100%' }}
                          onChange={(n) => updateLine(r.key, { thisPeriodQty: Number(n) || 0 })}
                        />
                      ),
                    },
                    {
                      title: 'Cumulative qty',
                      key: 'cumQty',
                      width: 110,
                      render: (_v: unknown, r: IpcLineDraft) =>
                        round2((r.previousQty ?? 0) + (r.thisPeriodQty || 0)),
                    },
                  ]
                : []),
              {
                title: 'Line value',
                dataIndex: 'lineAmount',
                width: 120,
                render: (v) => money(v, currency),
              },
              {
                title: 'Prev certified',
                dataIndex: 'previousCertified',
                width: 120,
                render: (v) => money(v, currency),
              },
              {
                title: 'Remaining',
                dataIndex: 'remaining',
                width: 110,
                render: (v) => money(v, currency),
              },
              {
                title: 'Basis',
                key: 'basis',
                width: 170,
                render: (_v: unknown, r: IpcLineDraft) => {
                  const ev = evidenceOf(r);
                  if (ev?.milestone) {
                    if (ev.milestone.achieved === null) return <Text type="secondary">{ev.milestone.number}</Text>;
                    return ev.milestone.achieved ? (
                      <Tag color="green">
                        {ev.milestone.number} achieved
                        {ev.milestone.actualDate ? ` ${ev.milestone.actualDate}` : ''}
                      </Tag>
                    ) : (
                      <Tag color="gold">{ev.milestone.number} not achieved</Tag>
                    );
                  }
                  if (ev?.matched && ev.siteCumulativePct != null) {
                    return (
                      <Tooltip
                        title={
                          <div>
                            {ev.packages.map((p) => (
                              <div key={p.id}>
                                {p.packageNumber}: {p.sitePct}%{p.lastLogDate ? ` (last log ${p.lastLogDate})` : ''}
                              </div>
                            ))}
                          </div>
                        }
                      >
                        <Space direction="vertical" size={0}>
                          <span>
                            Site {ev.siteCumulativePct}% · prev {ev.previousCertifiedPct}%
                          </span>
                          <Text type="secondary">
                            Suggest {ev.suggestedThisPeriodPct ?? 0}%
                            {ev.basis === 'PACKAGE' ? ' (package %)' : ''}
                          </Text>
                          <Link to="/epc/execution?tab=execution-packages">
                            {ev.packages.length} package{ev.packages.length === 1 ? '' : 's'}
                          </Link>
                        </Space>
                      </Tooltip>
                    );
                  }
                  if (r.sovLineId && evidence?.available) {
                    return (
                      <Text type="secondary">
                        {ev?.wbsCode ? `No package on WBS ${ev.wbsCode}` : 'No WBS code on SOV line'}
                      </Text>
                    );
                  }
                  return <Text type="secondary">—</Text>;
                },
              },
              {
                title: 'This period %',
                dataIndex: 'thisPeriodPct',
                width: 120,
                render: (v, r) => (
                  <InputNumber
                    min={0}
                    max={capped(r) ? 100 : undefined}
                    value={v}
                    disabled={!editable}
                    style={{ width: '100%' }}
                    onChange={(n) => updateLine(r.key, { thisPeriodPct: Number(n) || 0 })}
                  />
                ),
              },
              {
                title: 'This period amount',
                dataIndex: 'appliedAmount',
                width: 140,
                render: (v, r) => (
                  <InputNumber
                    min={0}
                    max={capped(r) ? r.remaining : undefined}
                    value={v}
                    disabled={!editable}
                    precision={2}
                    style={{ width: '100%' }}
                    onChange={(n) => updateLine(r.key, { appliedAmount: Number(n) || 0 })}
                  />
                ),
              },
              {
                title: 'Cumulative',
                width: 150,
                render: (_v, r) => {
                  const severity = overClaimOf(r);
                  return (
                    <Space direction="vertical" size={0}>
                      <span>{money(r.previousCertified + r.appliedAmount, currency)}</span>
                      {severity === 'OVER_CLAIM' ? <Tag color="red">Over 100%</Tag> : null}
                      {severity === 'OVER_MEASURE' ? <Tag color="orange">Over-measured</Tag> : null}
                      {unachievedMilestoneOf(r) ? (
                        <Tag color="gold">{unachievedMilestoneOf(r)} not achieved</Tag>
                      ) : null}
                      {aboveSite.includes(r) ? <Tag color="volcano">Above site progress</Tag> : null}
                      {withoutEvidence.includes(r) ? <Tag color="orange">No evidence</Tag> : null}
                    </Space>
                  );
                },
              },
              {
                title: 'Evidence',
                key: 'evidence',
                width: 200,
                render: (_v: unknown, r: IpcLineDraft) => (
                  <Space direction="vertical" size={4} style={{ width: '100%' }}>
                    <Input
                      size="small"
                      placeholder="Ref (MS / IR / survey no.)"
                      value={r.evidenceRef}
                      disabled={!editable}
                      onChange={(e) => updateLine(r.key, { evidenceRef: e.target.value })}
                    />
                    <Input
                      size="small"
                      placeholder="Note"
                      value={r.evidenceNote}
                      disabled={!editable}
                      onChange={(e) => updateLine(r.key, { evidenceNote: e.target.value })}
                    />
                  </Space>
                ),
              },
            ]}
          />
          {overClaims.length ? (
            <Alert
              type={overClaims.some((x) => x.severity === 'OVER_CLAIM') ? 'error' : 'warning'}
              showIcon
              style={{ marginTop: 12 }}
              message={`${overClaims.length} line${overClaims.length === 1 ? '' : 's'} above the contract line value`}
              description={
                <div>
                  <ul style={{ margin: 0, paddingLeft: 18 }}>
                    {overClaims.map(({ ln, severity }) => (
                      <li key={ln.key}>
                        {ln.lineCode || ln.description}:{' '}
                        {ln.lineAmount > 0
                          ? `${round2(((ln.previousCertified + ln.appliedAmount) / ln.lineAmount) * 100)}% of the line`
                          : ''}
                        {severity === 'OVER_CLAIM'
                          ? ' - should not exceed 100% without an approved Variation.'
                          : ' - over-measure allowed; keep measurement evidence.'}
                      </li>
                    ))}
                  </ul>
                  <Text type="secondary">Saving is allowed; the approver sees these warnings on the application.</Text>
                </div>
              }
            />
          ) : null}
          {unachievedClaims.length ? (
            <Alert
              type="warning"
              showIcon
              style={{ marginTop: 12 }}
              message="Claiming milestones not yet marked achieved"
              description={`${unachievedClaims
                .map((ln) => `${ln.lineCode || ln.description} (${unachievedMilestoneOf(ln)})`)
                .join(', ')}. Mark the milestone achieved on the contract Milestones tab once the event is evidenced.`}
            />
          ) : null}
          {aboveSite.length ? (
            <Alert
              type="warning"
              showIcon
              style={{ marginTop: 12 }}
              message="Claim ahead of site progress"
              description={`${aboveSite
                .map((ln) => `${ln.lineCode || ln.description} (claimed ${cumulativePctOf(ln)}%, site ${evidenceOf(ln)?.siteCumulativePct}%)`)
                .join(', ')}. Attach measurement evidence or update the daily logs before submitting.`}
            />
          ) : null}
          {withoutEvidence.length ? (
            <Alert
              type="warning"
              showIcon
              style={{ marginTop: 12 }}
              message="Claimed lines without site progress or an evidence reference"
              description={`${withoutEvidence
                .map((ln) => ln.lineCode || ln.description)
                .join(', ')}. Add the measurement sheet / inspection / survey reference in the Evidence column.`}
            />
          ) : null}
          {feeCheck ? (
            <Alert
              type="warning"
              showIcon
              style={{ marginTop: 12 }}
              message="Fee does not match the contract fee %"
              description={`Fee claimed ${money(feeCheck.fee, currency)}; ${feeCheck.pct}% of the ${money(
                feeCheck.cost,
                currency
              )} cost claimed is ${money(feeCheck.expected, currency)}.`}
            />
          ) : null}
          {!lines.length ? (
            <Alert
              type="warning"
              showIcon
              style={{ marginTop: 12 }}
              message="No entitlement lines"
              description="Set contract value / baseline or add milestones so IPC can seed progress lines."
            />
          ) : null}
        </Card>

        <Card title="Commercial deductions" style={{ marginBottom: 16 }}>
          <Row gutter={16} align="middle" style={{ marginBottom: 8 }}>
            <Col>
              <Text>Advance recovery by rate %</Text>
            </Col>
            <Col>
              <Switch checked={useAdvanceRate} onChange={setUseAdvanceRate} disabled={!editable} />
            </Col>
          </Row>
          <Row gutter={16}>
            {useAdvanceRate ? (
              <Col xs={24} md={8}>
                <Form.Item name="advanceRecoveryRatePct" label="Advance recovery %">
                  <InputNumber min={0} max={100} style={{ width: '100%' }} />
                </Form.Item>
              </Col>
            ) : (
              <Col xs={24} md={8}>
                <Form.Item name="advanceRecoveryAmount" label="Advance recovery amount">
                  <InputNumber min={0} precision={2} style={{ width: '100%' }} />
                </Form.Item>
              </Col>
            )}
            <Col xs={24} md={8}>
              <Form.Item name="remainingAdvanceBalance" label="Remaining advance balance">
                <InputNumber min={0} precision={2} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16} align="middle" style={{ marginBottom: 8 }}>
            <Col>
              <Text>Retention by rate %</Text>
            </Col>
            <Col>
              <Switch
                checked={useRetentionRate}
                onChange={setUseRetentionRate}
                disabled={!editable}
              />
            </Col>
          </Row>
          <Row gutter={16}>
            {useRetentionRate ? (
              <Col xs={24} md={8}>
                <Form.Item name="retentionRatePct" label="Retention %">
                  <InputNumber min={0} max={100} style={{ width: '100%' }} />
                </Form.Item>
              </Col>
            ) : (
              <Col xs={24} md={8}>
                <Form.Item name="retentionWithheldAmount" label="Retention withheld">
                  <InputNumber min={0} precision={2} style={{ width: '100%' }} />
                </Form.Item>
              </Col>
            )}
            <Col xs={24} md={8}>
              <Form.Item name="retentionReleasedAmount" label="Retention released">
                <InputNumber min={0} precision={2} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item name="retentionCap" label="Retention remaining room (cap)">
                <InputNumber min={0} precision={2} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>

          <Descriptions size="small" bordered column={2}>
            <Descriptions.Item label="Work gross (this period)">
              {money(preview.grossApplied, currency)}
            </Descriptions.Item>
            <Descriptions.Item label="Advance recovery">
              {money(preview.advanceRecovery, currency)}
            </Descriptions.Item>
            <Descriptions.Item label="Retention withheld">
              {money(preview.retentionWithheld, currency)}
            </Descriptions.Item>
            <Descriptions.Item label="Retention released">
              {money(preview.retentionReleased, currency)}
            </Descriptions.Item>
            <Descriptions.Item label="Net applied (preview)">
              <Text strong>{money(preview.netApplied, currency)}</Text>
            </Descriptions.Item>
            <Descriptions.Item label="Cumulative gross (to date)">
              {money(previousGross + preview.grossApplied, currency)}
            </Descriptions.Item>
          </Descriptions>
        </Card>

        <Space wrap style={{ marginBottom: 24 }}>
          {editable ? (
            <Button
              type="primary"
              htmlType="submit"
              icon={<SaveOutlined />}
              loading={saveM.isPending}
            >
              {isNew ? 'Create IPC' : 'Save changes'}
            </Button>
          ) : null}
          {!isNew && (status === 'Draft' || status === 'Rejected') ? (
            <Button loading={submitM.isPending} onClick={() => submitM.mutate()}>
              Submit for approval
            </Button>
          ) : null}
          {!isNew &&
          ['Approved', 'Certified'].includes(status) &&
          !linkedCert ? (
            <Button loading={createCertM.isPending} onClick={() => createCertM.mutate()}>
              Create certification
            </Button>
          ) : null}
          {linkedCert && certStatus !== 'Certified' ? (
            <Button
              loading={confirmCertM.isPending}
              onClick={() => confirmCertM.mutate(String(linkedCert.id))}
            >
              Confirm certification
            </Button>
          ) : null}
          {linkedCert && certStatus === 'Certified' && !siId ? (
            <Button type="primary" onClick={() => setConvertOpen(true)}>
              Convert to Draft SI
            </Button>
          ) : null}
          {siId ? (
            <Link to={`/growth/invoices/${siId}`}>
              Open Tax Invoice
              {linkedCert?.sales_invoice_doc_number
                ? ` (${String(linkedCert.sales_invoice_doc_number)})`
                : ''}
            </Link>
          ) : null}
        </Space>
      </Form>

      {!isNew ? (
        <div style={{ marginBottom: 16 }}>
          <EntityAttachmentsCard
            entityType={DOCUMENT_ENTITY_TYPE_EPC_PAYMENT_APPLICATION}
            entityId={paId!}
            title="Measurement & progress evidence"
            readOnly={certStatus === 'Certified'}
            queryKeyPrefix={['documents', 'epc-payment-application']}
            uploadHint="Measurement sheets, inspection reports, survey records, site photos — PDF, images or Office files, up to 25MB each."
          />
        </div>
      ) : (
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
          message="Save the IPC to attach measurement sheets, inspection reports and site photos."
        />
      )}

      {!isNew ? (
        <Card title="Approval" style={{ marginBottom: 16 }}>
          <ApprovalPanel entityType={ENTITY_TYPE_EPC_PAYMENT_APPLICATION} entityId={paId!} />
        </Card>
      ) : null}

      <ConvertCertificationToSalesInvoiceModal
        open={convertOpen}
        confirming={convertSiM.isPending}
        defaultDepartmentId={
          form.getFieldValue('department_id') ||
          (defaults?.departmentId ? String(defaults.departmentId) : null)
        }
        onCancel={() => setConvertOpen(false)}
        onConfirm={(p) => convertSiM.mutate(p.departmentId)}
      />
    </div>
  );
};

export default EpcPaymentApplicationFormPage;
